// LandingPage.js
import React, { useState, useRef, lazy, Suspense, useEffect } from 'react';
import './LandingPage.css'
import { Link } from 'react-router-dom';
import { Parallax, ParallaxProvider } from 'react-scroll-parallax';
import { Helmet } from 'react-helmet';
import Navbar from './Navbar';
import Toast from '../UI/Toast';
import Typewriter from '../UI/Typewriter';

const Footer = lazy(() => import('./Footer'));

const LandingPage = () => {

    // Déclencher l'événement Facebook Pixel pour la landing page
    useEffect(() => {
        if (window.fbq) {
            window.fbq('track', 'PageView');
        }
    }, []);

    const recaptchaRef = useRef();
    const [openIndex, setOpenIndex] = useState(null);
    const [toast, setToast] = useState({ show: false, type: '', message: '' });
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [activeStyle, setActiveStyle] = useState('Professional');

    const togglePanel = (index) => {
        if (openIndex === index) {
            setOpenIndex(null);
        } else {
            setOpenIndex(index);
        }
    };

    const texts = ["an essay", "an article", "a blog post", "an academic paper", "a complete book"];


    const stylePhrases = {
        Professional: 'Transform your writing process with Book Witch\'s AI-powered platform. Join thousands of authors who\'ve already enhanced their productivity by 3x.',
        Creative: 'Unleash your creativity while our AI handles the heavy lifting. From first draft to final polish, your unique voice shines through every word.',
        Academic: 'Leverage advanced AI technology to streamline your academic writing. Perfect for researchers and students seeking publication-ready content.',
        Storytelling: 'Craft compelling narratives effortlessly. Our AI adapts to your style, helping you weave stories that leave readers spellbound.',
        Business: 'Accelerate your content creation while maintaining consistent brand voice. Join industry leaders who\'ve scaled their content strategy with Book Witch.',
        Conversational: 'Write like you talk, let AI perfect the delivery. It\'s like having a professional editor who knows your style inside out.',
        Technical: 'Generate precise, well-structured content with technical accuracy. Ideal for documentation that speaks with clarity and authority.',
        Marketing: 'Create converting copy that resonates with your audience. From social media to long-form content, stay consistently on-brand.',
        Journalistic: 'Deliver factual, engaging content with journalistic precision. Meet tight deadlines without compromising on quality.'
    };

    const handleClick = (style) => {
        setActiveStyle(style);
    };

    const handleDescriptionChange = (e) => {
        const value = e.target.value;
        localStorage.setItem('currentDescription', value);
    };

    const faqItems = [
        {
            icon: "fas fa-pen",
            question: "How does Book Witch preserve my writing style?",
            answer: "Book Witch features a unique style learning system that analyzes your writing samples (500-2000 characters) to understand and replicate your voice. Whether you're expanding content, rewriting, or generating new material, our AI maintains your distinctive style while enhancing quality and flow."
        },
        {
            icon: "fas fa-fingerprint",
            question: "Is the content truly original and plagiarism-free?",
            answer: "Absolutely! Every piece of content generated by Book Witch is 100% original. Our AI creates unique content from scratch rather than rephrasing existing text. This ensures your work passes plagiarism checks and maintains authenticity while meeting academic and professional standards."
        },
        {
            icon: "fas fa-globe",
            question: "What languages and writing formats are supported?",
            answer: "Book Witch supports 30+ languages including English, French, Spanish, German, Italian, and Portuguese. You can create any type of content - from academic papers to creative writing, business documents to blog posts. The AI adapts its style and tone to match your chosen format."
        },
        {
            icon: "fas fa-sliders-h",
            question: "What editing and customization options are available?",
            answer: "Book Witch offers comprehensive editing tools including content expansion, shortening, paraphrasing with different tones, and custom prompts for specific requirements. You can also personalize the AI to match your writing style, adjust content length, and export to multiple formats (PDF, DOCX, TXT)."
        },
        {
            icon: "fas fa-credit-card",
            question: "How does the pricing work?",
            answer: "We offer a free plan with 3000 words monthly. Premium plans come with a 7-day money-back guarantee. If the tool does not meet your needs within 7 days of signing up, you can request a refund via email, no questions asked. All plans include unlimited words, advanced editing tools, and priority support. You can upgrade, downgrade, or cancel anytime. All plans include our core AI writing and editing features."
        },
        {
            icon: "fas fa-bolt",
            question: "How fast can I create content with Book Witch?",
            answer: "Most users see immediate results, with writing time reduced by up to 70%. The AI can generate full chapters in seconds, while editing tools provide instant improvements. Our users typically complete projects 3x faster without sacrificing quality."
        },
        {
            icon: "fas fa-lock",
            question: "Is my content secure and private?",
            answer: "Your privacy is our priority. All content created with Book Witch remains 100% yours and is never used to train our AI. We use enterprise-grade encryption for data storage and transfer, ensuring your work stays confidential and secure."
        },
        {
            icon: "fas fa-graduation-cap",
            question: "Can I use Book Witch for academic writing?",
            answer: "Yes! Book Witch is perfect for academic writing. It helps structure research papers, maintains academic tone, and ensures proper citations. The AI understands academic conventions and can adapt to different citation styles while maintaining scholarly standards."
        }
    ];

    return (
        <>
            <Helmet>
                <title>Book Witch - Undetectable AI Writer</title>
                
                {/* Open Graph Meta Tags */}
                <meta property="og:type" content="website" />
                <meta property="og:title" content="Book Witch: AI-Powered Book & eBook Writing Assistant" />
                <meta property="og:description"
                    content="Book Witch helps you create professional books and essays with AI. Transform your ideas, write faster, and enhance your content quality." />
                <meta property="og:url" content="https://bookwitch.io" />
                <meta property="og:image" content="https://bookwitch.io/images/android-chrome-512x512.png" />
                <meta property="og:site_name" content="Book Witch" />
                
                {/* Twitter Card Meta Tags */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content="Book Witch: AI-Powered Book & eBook Writing Assistant" />
                <meta name="twitter:description"
                    content="Book Witch helps you create professional books and essays with AI. Transform your ideas, write faster, and enhance your content quality." />
                <meta name="twitter:image" content="https://bookwitch.io/images/android-chrome-512x512.png" />
                <meta name="twitter:site" content="@BookWitchAI" />
                
                {/* Additional SEO Meta Tags */}
                <meta name="keywords"
                    content="AI Writer, AI writing assistant, book writing software, ebook creator, AI author tools, writing automation, book generator, essay writer, AI writing platform" />
                
                {/* Schema.org structured data */}
                <script type="application/ld+json">
                {`
                {
                  "@context": "https://schema.org",
                  "@type": "SoftwareApplication",
                  "name": "Book Witch",
                  "applicationCategory": "WritingApplication",
                  "operatingSystem": "Web",
                  "description": "Book Witch is your AI-powered writing assistant. Transform your ideas into professional books, essays, and ebooks. Write faster and smarter with advanced AI Writer.",
                  "image": "https://bookwitch.io/logo_dark.png",
                  "url": "https://bookwitch.io",
                  "availableOnDevice": "Any device with a web browser",
                  "potentialAction": {
                    "@type": "UseAction",
                    "target": {
                      "@type": "EntryPoint",
                      "urlTemplate": "https://bookwitch.io/app"
                    }
                  },
                  "offers": {
                    "@type": "AggregateOffer",
                    "priceCurrency": "EUR",
                    "lowPrice": "0",
                    "highPrice": "26.00",
                    "offerCount": "7",
                    "offers": [
                      {
                        "@type": "Offer",
                        "name": "Freemium",
                        "price": "0",
                        "priceCurrency": "EUR"
                      },
                      {
                        "@type": "Offer",
                        "name": "Book Witch Premium",
                        "priceCurrency": "EUR",
                        "price": "26.00",
                        "priceValidUntil": "2025-12-31",
                        "billingDuration": "P1M"
                      },
                      {
                        "@type": "Offer",
                        "name": "Book Witch Premium",
                        "priceCurrency": "USD",
                        "price": "27.00",
                        "priceValidUntil": "2025-12-31",
                        "billingDuration": "P1M"
                      },
                      {
                        "@type": "Offer",
                        "name": "Book Witch Premium",
                        "priceCurrency": "INR",
                        "price": "1100.00",
                        "priceValidUntil": "2025-12-31",
                        "billingDuration": "P1M"
                      },
                      {
                        "@type": "Offer",
                        "name": "Book Witch Premium",
                        "priceCurrency": "BRL",
                        "price": "87.00",
                        "priceValidUntil": "2025-12-31",
                        "billingDuration": "P1M"
                      },
                      {
                        "@type": "Offer",
                        "name": "Book Witch Premium",
                        "priceCurrency": "NGN",
                        "price": "14200.00",
                        "priceValidUntil": "2025-12-31",
                        "billingDuration": "P1M"
                      },
                      {
                        "@type": "Offer",
                        "name": "Book Witch Premium",
                        "priceCurrency": "TRY",
                        "price": "330.00",
                        "priceValidUntil": "2025-12-31",
                        "billingDuration": "P1M"
                      },
                      {
                        "@type": "Offer",
                        "name": "Book Witch Premium",
                        "priceCurrency": "ARS",
                        "price": "2400.00",
                        "priceValidUntil": "2025-12-31",
                        "billingDuration": "P1M"
                      }
                    ]
                  },
                  "author": {
                    "@type": "Person",
                    "name": "Book Witch Creator"
                  },
                  "knowsAbout": ["AI Writing", "Natural Language Processing", "Content Creation"]
                }
                `}
                </script>
                
                {/* Schema.org Organization data */}
                <script type="application/ld+json">
                {`
                {
                  "@context": "https://schema.org",
                  "@type": "Organization",
                  "name": "Book Witch",
                  "url": "https://bookwitch.io",
                  "logo": "https://bookwitch.io/logo_dark.png"
                }
                `}
                </script>
                
                {/* Schema.org WebPage data */}
                <script type="application/ld+json">
                {`
                {
                  "@context": "https://schema.org",
                  "@type": "WebPage",
                  "name": "Book Witch: AI-Powered Book & eBook Writing Assistant",
                  "description": "Book Witch is your AI-powered writing assistant. Transform your ideas into professional books, essays, and ebooks. Write faster and smarter with advanced AI Writer.",
                  "url": "https://bookwitch.io",
                  "speakable": {
                    "@type": "SpeakableSpecification",
                    "cssSelector": ["h1", "h2", ".hero-content"]
                  },
                  "mainEntity": {
                    "@type": "SoftwareApplication",
                    "name": "Book Witch",
                    "applicationCategory": "WritingApplication"
                  }
                }
                `}
                </script>
                
                {/* Schema.org Person data (for individual entrepreneur) */}
                <script type="application/ld+json">
                {`
                {
                  "@context": "https://schema.org",
                  "@type": "Person",
                  "name": "Book Witch Creator",
                  "url": "https://bookwitch.io",
                  "jobTitle": "Independent Software Developer",
                  "worksFor": {
                    "@type": "Organization",
                    "name": "Book Witch"
                  },
                  "knowsAbout": ["AI Writing", "Natural Language Processing", "Content Creation"]
                }
                `}
                </script>
                
                {/* Schema.org FAQ data */}
                <script type="application/ld+json">
                {`
                {
                  "@context": "https://schema.org",
                  "@type": "FAQPage",
                  "mainEntity": [
                    {
                      "@type": "Question",
                      "name": "How does Book Witch preserve my writing style?",
                      "acceptedAnswer": {
                        "@type": "Answer",
                        "text": "Book Witch features a unique style learning system that analyzes your writing samples (500-2000 characters) to understand and replicate your voice. Whether you're expanding content, rewriting, or generating new material, our AI maintains your distinctive style while enhancing quality and flow."
                      }
                    },
                    {
                      "@type": "Question",
                      "name": "Is the content truly original and plagiarism-free?",
                      "acceptedAnswer": {
                        "@type": "Answer",
                        "text": "Absolutely! Every piece of content generated by Book Witch is 100% original. Our AI creates unique content from scratch rather than rephrasing existing text. This ensures your work passes plagiarism checks and maintains authenticity while meeting academic and professional standards."
                      }
                    },
                    {
                      "@type": "Question",
                      "name": "What languages and writing formats are supported?",
                      "acceptedAnswer": {
                        "@type": "Answer",
                        "text": "Book Witch supports 30+ languages including English, French, Spanish, German, Italian, and Portuguese. You can create any type of content - from academic papers to creative writing, business documents to blog posts. The AI adapts its style and tone to match your chosen format."
                      }
                    },
                    {
                      "@type": "Question",
                      "name": "What editing and customization options are available?",
                      "acceptedAnswer": {
                        "@type": "Answer",
                        "text": "Book Witch offers comprehensive editing tools including content expansion, shortening, paraphrasing with different tones, and custom prompts for specific requirements. You can also personalize the AI to match your writing style, adjust content length, and export to multiple formats (PDF, DOCX, TXT)."
                      }
                    },
                    {
                      "@type": "Question",
                      "name": "How does the pricing work?",
                      "acceptedAnswer": {
                        "@type": "Answer",
                        "text": "We offer a free plan with 3000 words monthly. Premium plans come with a 7-day money-back guarantee. If the tool does not meet your needs within 7 days of signing up, you can request a refund via email, no questions asked. All plans include unlimited words, advanced editing tools, and priority support. You can upgrade, downgrade, or cancel anytime. All plans include our core AI writing and editing features."
                      }
                    }
                  ]
                }
                `}
                </script>
            </Helmet>
            <ParallaxProvider>
                <Navbar />

                <Parallax className='parallax-bg' speed={-30}>
                    <img
                        src="/background.webp"
                        alt="Abstract background with geometric patterns"
                        className="parallax-image"
                        width="1920"
                        height="1080"
                    />
                </Parallax>

                <Parallax className='landing-page'>

                    <div className="section hero-section">
                        <div className="hero-content">
                            <h1>
                                Turn Your Ideas into <span className="highlight"><Typewriter as='span' className='' texts={["Books", "Stories", "Essays", "Articles", "Ebooks"]} typeSpeed={80} deleteSpeed={50} period={1200} /></span>
                            </h1>
                            <h1 style={{ margin: "-20px 0 30px 0", padding: 0 }}>Without the Struggle</h1>
                            <h2>Book Witch is the ultimate AI-powered book writer & editor. Generate full-length ebooks in one click, refine them with ease, and finally bring your words to life — faster and smarter than ever.</h2>

                            <Link to="/app" className="no-style-link">
                                <button className="button-important">
                                    <div className="inner">
                                        Start your first book today — it’s free
                                    </div>
                                </button>
                            </Link>
                            <p className="social-proof">
                                <span>💡 Over 5,000+ writers already streamlined their writing with Book Witch</span>
                            </p>
                        </div>

                        <video
                            muted
                            autoPlay
                            loop
                            preload="none"
                            poster="/video-poster.jpg"
                            className="hero-video glowAnime"
                        >
                            <source src="/demo.mp4" type="video/mp4" />
                            <track kind="captions" src="/captions.vtt" label="Français" />
                        </video>

                        <div className="trusted-by-section">
                            <div className="trusted-by-carousel">
                                <div className="carousel-container">
                                    <div className="carousel-track">
                                        <div className="carousel-item">
                                            <a href="https://theresanaiforthat.com/ai/bookwitch/?ref=featured&v=139162" target="_blank" rel="nofollow">
                                                <img
                                                    width="300"
                                                    height="60"
                                                    src="https://media.theresanaiforthat.com/featured-on-taaft.png?width=600"
                                                    alt="Featured on There's an AI for That"

                                                />
                                            </a>
                                        </div>
                                        <div className="carousel-item">
                                            <a href="https://topai.tools/t/book-witch?ref=embed">
                                                <img
                                                    src="https://topai.tools/assets/img/topai.tools.gif"
                                                    alt="Book Witch Featured on topAI.tools"
                                                    width="300"
                                                    height="60"

                                                />
                                            </a>
                                        </div>
                                        <div className="carousel-item">
                                            <a href="https://aitoolsmarketer.com/">
                                                <img
                                                    src="ai-tools-logo-light.png"
                                                    alt="Book Witch Featured on topAI.tools"
                                                    width="300"
                                                    height="60"
                                                />
                                            </a>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="section intro-section">
                        <div className="section-header">
                            <h2>Why <span className="highlight">Book Witch</span> Exists</h2>
                        </div>
                        <div className="two-col">
                            <div className="image">
                                <img src="/images/process.webp" alt="Blank page to finished book" />
                            </div>
                            <div className="content">
                                <p>
                                    We all know the struggle. Staring at a blank page, waiting for inspiration. Drafting for hours, only to end up with something that doesn’t feel right. Writing a book used to take months — sometimes years.
                                </p>
                                <p>
                                    With Book Witch, that frustration disappears. Imagine opening your laptop, typing a single idea… and within minutes holding a fully structured book draft in your hands. Chapters, subchapters, clean formatting — ready to refine or publish.
                                </p>
                                <p>
                                    Book Witch doesn’t just write. It helps you create, finish, and share — without the endless grind.
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="section audience-section" id="audience">
                        <div className="section-header">
                            <h4>WHO IS BOOK WITCH FOR?</h4>
                            <h2>Perfect for Every <span className="highlight">Writing Need</span></h2>
                        </div>

                        <div className="audience-grid">
                            <div className="audience-item">
                                <div className="audience-image">
                                    <img src="/images/author.webp" alt="Authors and writers" />
                                </div>
                                <div className="audience-content">
                                    <div className="audience-icon">
                                        <i className="fas fa-book"></i>
                                    </div>
                                    <h3>Authors & Non-Fiction Writers</h3>
                                    <p>Dreaming of publishing your book but stuck in endless drafts? Generate a complete ebook from a single prompt, or polish your manuscript with our AI-powered editor.</p>
                                </div>
                            </div>

                            <div className="audience-item">
                                <div className="audience-image">
                                    <img src="/images/student.webp" alt="Students and academics" />
                                </div>
                                <div className="audience-content">
                                    <div className="audience-icon">
                                        <i className="fas fa-graduation-cap"></i>
                                    </div>
                                    <h3>Students & Academics</h3>
                                    <p>No more all-nighters or messy structures. Book Witch helps you create clear, well-structured essays and research papers effortlessly.</p>
                                </div>
                            </div>

                            <div className="audience-item">
                                <div className="audience-image">
                                    <img src="/images/business.webp" alt="Businesses and marketers" />
                                </div>
                                <div className="audience-content">
                                    <div className="audience-icon">
                                        <i className="fas fa-briefcase"></i>
                                    </div>
                                    <h3>Businesses & Marketers</h3>
                                    <p>From reports to blog posts to sales emails — Book Witch creates content that’s clear, compelling, and professional in seconds.</p>
                                </div>
                            </div>

                            <div className="audience-item">
                                <div className="audience-image">
                                    <img src="/images/journalist.webp" alt="Bloggers and journalists" />
                                </div>
                                <div className="audience-content">
                                    <div className="audience-icon">
                                        <i className="fas fa-newspaper"></i>
                                    </div>
                                    <h3>Bloggers & Journalists</h3>
                                    <p>Turn your ideas into polished articles faster than ever, so you can spend more time on your voice and less time on the grind.</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="section features-section" id="features">
                        <div className="section-header">
                            <h4>THE AI-POWERED WRITING EXPERIENCE</h4>
                            <h2>Your Personal AI Writing <span className="highlight">Companion</span></h2>
                        </div>

                        <div className="features-grid">
                            <div className="feature-item">
                                <div className="icon">
                                    <i className="fas fa-book-open"></i>
                                </div>
                                <div>
                                    <h4>One-Click Ebook Generator</h4>
                                    <p>Just enter a short description. Book Witch instantly builds a full-length ebook — with chapters, subchapters, and flow that makes sense.</p>
                                </div>
                            </div>

                            <div className="feature-item">
                                <div className="icon">
                                    <i className="fas fa-edit"></i>
                                </div>
                                <div>
                                    <h4>Smart AI Editor</h4>
                                    <p>Expand, rewrite, summarize, or perfect your drafts while keeping your unique voice intact. It’s like having a co-writer who knows exactly how you think.</p>
                                </div>
                            </div>

                            <div className="feature-item">
                                <div className="icon">
                                    <i className="fas fa-language"></i>
                                </div>
                                <div>
                                    <h4>Multilingual Writing</h4>
                                    <p>Craft essays, blogs, and books in 30+ languages with near-native fluency. Break language barriers, share your words globally.</p>
                                </div>
                            </div>

                            <div className="feature-item">
                                <div className="icon">
                                    <i className="fas fa-file-export"></i>
                                </div>
                                <div>
                                    <h4>Seamless Export Options</h4>
                                    <p>PDF, DOCX, TXT — download with a click and share anywhere.</p>
                                </div>
                            </div>

                            <div className="feature-item">
                                <div className="icon">
                                    <i className="fas fa-brain"></i>
                                </div>
                                <div>
                                    <h4>AI That Learns Your Style</h4>
                                    <p>Every word you write makes Book Witch smarter. It adapts to your style so your writing always sounds authentically you.</p>
                                </div>
                            </div>

                            <div className="feature-item">
                                <div className="icon">
                                    <i className="fas fa-shield-alt"></i>
                                </div>
                                <div>
                                    <h4>100% Secure & Original</h4>
                                    <p>Your words are yours alone. No plagiarism, no re-use, no compromise.</p>
                                </div>
                            </div>

                            {/* Context, Guidelines, References */}
                            <div className="feature-item">
                                <div className="icon">
                                    <i className="fas fa-clipboard-list"></i>
                                </div>
                                <div>
                                    <h4>Rich Context Control</h4>
                                    <p>Provide a project brief or persistent context so the AI stays on-topic and consistent across chapters.</p>
                                </div>
                            </div>

                            <div className="feature-item">
                                <div className="icon">
                                    <i className="fas fa-sliders-h"></i>
                                </div>
                                <div>
                                    <h4>Custom Guidelines & Style</h4>
                                    <p>Add writing guidelines and brand rules the AI will follow to keep tone and structure consistent.</p>
                                </div>
                            </div>

                            <div className="feature-item">
                                <div className="icon">
                                    <i className="fas fa-link"></i>
                                </div>
                                <div>
                                    <h4>Reference Materials</h4>
                                    <p>Attach sources, articles, or research. The AI uses them as references to inform and ground your content.</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* CTA banner after features */}
                    <div className="cta-section">
                        <div className="section">
                            <div className="cta-banner">
                                <div className="cta-kicker">READY TO TRANSFORM YOUR WRITING?</div>
                                <div className="cta-headline">Test Book Witch Free — No Credit Card Needed</div>
                                <div className="cta-actions">
                                    <Link to="/app" className="no-style-link">
                                        <button className="button-important">
                                            <div className="inner">Start your free trial</div>
                                        </button>
                                    </Link>
                                </div>
                                <div className="cta-note">Try all core features instantly</div>
                            </div>
                        </div>
                    </div>

                    <div className="section usage-section" id="usage">
                        <div className="section-header">
                            <h4>TWO WAYS TO USE BOOK WITCH</h4>
                            <h2>Two Ways to <span className="highlight">Write with Book Witch</span></h2>
                        </div>

                        <div className="usage-grid">
                            <div className="usage-item">
                                <div className="icon">
                                    <i className="fas fa-bolt"></i>
                                </div>
                                <div className="content">
                                    <h4>One-Click Ebook Generation</h4>
                                    <p>Got an idea? Turn it into a structured, full ebook in a single step. No outline needed.</p>
                                </div>
                            </div>

                            <div className="usage-item">
                                <div className="icon">
                                    <i className="fas fa-pencil-alt"></i>
                                </div>
                                <div className="content">
                                    <h4>AI Writing & Editing</h4>
                                    <p>Want more control? Use the editor to refine, expand, and polish your text with AI-powered assistance.</p>
                                </div>
                            </div>
                        </div>

                        <div className="usage-note">
                            <div className="note-inner">
                                <span className="note-icon">💡</span>
                                <p>Whichever way you choose, you'll write up to 3x faster while keeping full creative control.</p>
                            </div>
                        </div>
                    </div>

                    {/* NSFW section */}
                    <div className="section nsfw-section">
                        <div className="section-header">
                            <h4>CONTENT FREEDOM</h4>
                            <h2>Imagination Unleashed — <span className="highlight">NSFW Content</span> Supported</h2>
                            <p>
                                Create explicit content when your project requires it. No roadblocks: explore adult themes, erotic narratives, and mature topics with full creative control.
                            </p>
                        </div>
                        <div className="two-col">
                            <div className="content">
                                <p>
                                    Use the same powerful tools — context, guidelines, and references — to keep explicit content coherent, on-brand, and aligned to your intent.
                                </p>
                                <p>
                                    Your creative limits shouldn’t be defined by your tools. Book Witch lets you write boldly while keeping quality high.
                                </p>
                                <div className="actions">
                                    <Link to="/app" className="no-style-link">
                                        <button className="button-important">
                                            <div className="inner">Start writing — no limits</div>
                                        </button>
                                    </Link>
                                </div>
                            </div>
                            <div className="image">
                                <img src="/images/nsfw.webp" alt="NSFW content creation" />
                            </div>
                        </div>
                    </div>

                    <div className='section style-section'>
                        <div className="section-header">
                            <h4>CUSTOMIZATION</h4>
                            <h2>Control the tone and style <span className='underline'>your way</span></h2>
                            <p>
                                Simply write in your preferred language and let our AI handle the rest, delivering polished results.
                            </p>
                        </div>

                        <div className="style-buttons">
                            {Object.keys(stylePhrases).map((style) => (
                                <button
                                    key={style}
                                    className={activeStyle === style ? 'style-button active' : 'style-button'}
                                    onClick={() => handleClick(style)}
                                >
                                    {style}
                                </button>
                            ))}
                        </div>
                        <div className="style-result">
                            <p>{stylePhrases[activeStyle]}</p>
                        </div>
                        <p className="cta-subtext">Your words, your tone — perfectly polished every time.</p>
                    </div>

                    <div className="section speed-section">
                        <div className="section-header">
                            <h4>BUILT FOR SPEED & QUALITY</h4>
                            <h2>Complete a <span className="highlight">300-page book</span> in hours — not months</h2>
                        </div>
                        <div className="two-col">
                            <div className="image">
                                <img src="/images/book_witch_1.webp" alt="Fast writing with AI" />
                            </div>
                            <div className="content">
                                <p>
                                    With Book Witch, the impossible becomes achievable. Instead of losing momentum or drowning in rewrites, you’ll hold a professional-grade manuscript in days — structured, coherent, and original.
                                </p>
                                <p>
                                    Imagine showing your audience a polished 300-page book you created this week. Imagine submitting an essay in hours instead of pulling an all-nighter. Imagine finally publishing that book idea you’ve been sitting on for years.
                                </p>
                                <p>
                                    That’s what Book Witch unlocks.
                                </p>
                                <div className="actions">
                                    <Link to="/app" className="no-style-link">
                                        <button className="button-important">
                                            <div className="inner">
                                                Get 3,000 words for free • Start now
                                            </div>
                                        </button>
                                    </Link>
                                    <p className="cta-subtext">Join over 5,000 writers who are revolutionizing their workflow with Book Witch</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="cta-section">
                        <div className="section">
                            <div className="cta-banner">
                                <div className="cta-kicker">JOIN 5,000+ WRITERS</div>
                                <div className="cta-headline">Get 3,000 Words Free — Start Now</div>
                                <div className="cta-actions">
                                    <Link to="/app" className="no-style-link">
                                        <button className="button-important">
                                            <div className="inner">Start writing for free</div>
                                        </button>
                                    </Link>
                                </div>
                                <div className="cta-note">No credit card required</div>
                            </div>
                        </div>
                    </div>

                    <div className="section faq-section" id="faq">
                        <div className="section-header">
                            <h4>FAQ</h4>
                            <h2>Frequently Asked <span className="highlight">Questions</span></h2>
                        </div>

                        <div className="faq-grid">
                            {faqItems.map((item, index) => (
                                <div className="faq-item" key={index}>
                                    <div className="faq-question" onClick={() => togglePanel(index)}>
                                        <div className="question-content">
                                            <i className={item.icon}></i>
                                            <span>{item.question}</span>
                                        </div>
                                        <i className={`fas ${openIndex === index ? 'fa-chevron-up' : 'fa-chevron-down'}`}></i>
                                    </div>
                                    {openIndex === index && (
                                        <div className="faq-answer">
                                            {item.answer}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>

                    <Suspense fallback={<div>Loading...</div>}>
                        <Footer />
                    </Suspense>
                </Parallax>
            </ParallaxProvider>

            {/* Toast en dehors du ParallaxProvider */}
            {toast.show && (
                <Toast
                    type={toast.type}
                    message={toast.message}
                    onClose={() => setToast({ ...toast, show: false })}
                    duration={6000}
                />
            )}
        </>
    );
};

export default LandingPage;
